/**
 * AntD Table 通用包装 — 含 loading skeleton / empty state / 默认分页 / 列宽自适应。
 *
 * 9.2f mobile:`mobileMode='card'` + `renderMobileCard` 时,在 <576 渲染 stacked
 * 卡片列表(替 Table 横滚);默 `'scroll'` 保现 AntD Table(零回归)。
 */
import type { ReactNode } from 'react';
import { Empty, Skeleton, Table } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';

import { useBreakpoint } from '@/lib/breakpoints';

interface DataTableProps<T> {
  columns: ColumnsType<T>;
  data?: T[];
  rowKey: keyof T | ((row: T) => string);
  loading?: boolean;
  pageSize?: number;
  emptyText?: string;
  className?: string;
  onRowClick?: (row: T) => void;
  /** 9.2f: mobile(<576)渲染策略。默 'scroll'(现 AntD Table 横滚)。 */
  mobileMode?: 'card' | 'scroll';
  /** mobileMode='card' 时每行渲染为 stacked 卡片。 */
  renderMobileCard?: (row: T, index: number) => ReactNode;
}

export default function DataTable<T extends object>({
  columns,
  data,
  rowKey,
  loading = false,
  pageSize = 20,
  emptyText = '暂无数据',
  className = '',
  onRowClick,
  mobileMode = 'scroll',
  renderMobileCard,
}: DataTableProps<T>) {
  const { isMobile } = useBreakpoint();

  if (loading) return <Skeleton active paragraph={{ rows: pageSize / 2 || 6 }} />;
  if (!data || data.length === 0) return <Empty description={emptyText} />;

  const keyOf = (row: T, i: number): string =>
    typeof rowKey === 'function' ? rowKey(row) : String((row as Record<string, unknown>)[rowKey as string] ?? i);

  // 9.2f mobile card 模式
  if (isMobile && mobileMode === 'card' && renderMobileCard) {
    return (
      <div className={`space-y-2.5 ${className}`}>
        {data.map((row, i) => (
          <div
            key={keyOf(row, i)}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            className={`rounded-lg border border-slate-100 bg-white p-3 shadow-card ${
              onRowClick ? 'cursor-pointer active:bg-slate-50' : ''
            }`}
          >
            {renderMobileCard(row, i)}
          </div>
        ))}
      </div>
    );
  }

  const pagination: false | TablePaginationConfig =
    data.length > pageSize
      ? { pageSize, showSizeChanger: true, showQuickJumper: true }
      : false;

  return (
    <Table<T>
      className={className}
      columns={columns}
      dataSource={data}
      rowKey={rowKey as never}
      pagination={pagination}
      onRow={
        onRowClick
          ? (row) => ({ onClick: () => onRowClick(row), style: { cursor: 'pointer' } })
          : undefined
      }
      scroll={{ x: 'max-content' }}
      size="middle"
    />
  );
}
